import axiosInstance from "./axiosInstance";

export interface AccommodationApplicantApi {
  publicId: string;
  visaApplicationId: number;
  itsNo: string;
  applicantName: string;
  gender: string;
  age?: string | number;
  applicantType: string;
  vatan?: string;
  category?: string;
  photo?: string;
  photoUrl?: string;
}

export interface AccommodationRoomRequestApi {
  roomSelectionPublicId: string;
  roomAllocationId: number;
  roomAllocationPublicId: string;
  venuePublicId: string;
  venueName: string;
  checkInDate: string;
  checkOutDate: string;
  propertyId: number;
  propertyPublicId: string;
  propertyName: string;
  propertyRoomTypeId: number;
  roomTypeName: string;
  totalApplicants: number;
  status: string;
  serviceCount: number;
  applicants: AccommodationApplicantApi[];
  allocations: Array<Record<string, unknown>>;
}

export interface AccommodationTourApi {
  tourPublicId: string;
  tourCode: string;
  tourName: string;
  tourOperatorName: string;
  tourOperatorOfficeName: string;
  specialAccommodationRequest?: string;
  tourCategory: number;
  tourCategoryName: string;
  status: string;
  tourStatus: number;
  tourStatusName: string;
  numberOfApplicants: number;
  arrivalDate: string;
  departureDate: string;
  applicants: AccommodationApplicantApi[];
  roomRequests: Array<{
    venuePublicId: string;
    venueName: string;
    checkInDate: string;
    checkOutDate: string;
    propertyId: number;
    propertyPublicId: string;
    propertyName: string;
    roomRequests: AccommodationRoomRequestApi[];
  }>;
}

export interface AccommodationPropertyApi {
  propertyId: number;
  publicId: string;
  venuePublicId: string;
  venueName: string;
  propertyName: string;
  city: string;
  address: string;
  totalRooms: number;
  occupiedRooms: number;
  blockedRooms: number;
  availableRooms: number;
  occupancyPercentage: number;
  rooms: Array<{
    propertyRoomTypeId: number;
    publicId: string;
    roomTypeId: number;
    roomTypeName: string;
    roomNames: string[];
    totalRooms: number;
    occupiedRooms: number;
    blockedRooms: number;
    availableRooms: number;
    isAvailable: boolean;
    minOccupancy: number;
    maxOccupancy: number;
    maxAdults: number;
    maxChildren: number;
  }>;
}

export interface AccommodationDashboardDataApi {
  fromDate: string;
  toDate: string;
  todayMovement: {
    date: string;
    checkIns: number;
    checkOuts: number;
    totalRooms: number;
    occupiedRooms: number;
    occupancyPercentage: number;
  };
  tourRoomRequests: AccommodationTourApi[];
  properties: AccommodationPropertyApi[];
}

export interface DailyCheckInSummaryStatsApi {
  toursArrivingToday: number;
  totalApplicantsArriving: number;
  roomsRequestedToday: number;
  roomBreakdown: string;
}

interface DailyCheckInSummaryStatsResponse {
  success: boolean;
  message: string;
  data: DailyCheckInSummaryStatsApi | null;
  errors: unknown[];
}

export interface DailyCheckInStayApi {
  venuePublicId: string;
  venueName: string;
  propertyPublicId: string;
  propertyName: string;
  startDate: string;
  endDate: string;
  checkInTime: string | null;
  checkOutTime: string | null;
}

export interface DailyCheckInRoomAllocationApi {
  roomNumber?: string | number | null;
  roomTypeName?: string | null;
  venueName: string;
  propertyName: string;
  isActive?: boolean;
  applicants?: {
    visaApplicationId: number;
    isActive?: boolean;
  }[];
}

export interface DailyCheckInReportTourApi {
  tourId: number;
  tourName: string;
  tourType: number;
  tourTypeLabel: string;
  tourCategory: number;
  tourCategoryLabel: string;
  tourOperatorName: string;
  arrivalDate: string;
  arrivalTime: string | null;
  arrivalFlightNo: string | null;
  arrivalPortName: string | null;
  travelMeans: string | null;
  stayDetails: DailyCheckInStayApi[];
  fullDetails: {
    tour: {
      tourId: number;
      tourName: string;
      tourOperatorName: string;
      arrivalDate: string | null;
      arrivalTime: string | null;
      arrivalFlightNo: string | null;
      arrivalPortName: string | null;
      departureDate: string | null;
      departureTime: string | null;
      departureFlightNo: string | null;
      departurePortName: string | null;
      specialAccommodationRequest: string | null;
      roomAllocations?: DailyCheckInRoomAllocationApi[];
    };
    applicants: DailyCheckInApplicantApi[];
  } | null;
  noOfApplicants: number;
}

export interface DailyCheckInApplicantApi {
  visaApplicationId: number;
  itsNo: string | null;
  firstName: string | null;
  lastName: string | null;
  dateOfBirth: string | null;
  gender: string | null;
  nationality: string | null;
}

export interface DailyCheckInReportRequest {
  startDate: string;
  endDate: string;
  venuePublicId?: string;
  propertyPublicId?: string;
  isDetails: boolean;
  specificTourId?: number;
}

export const getDailyCheckInReport = async (
  params: DailyCheckInReportRequest,
): Promise<DailyCheckInReportTourApi[]> => {
  const response = await axiosInstance.get<{
    success: boolean;
    message: string;
    data: DailyCheckInReportTourApi[];
  }>("/accommodation/reports/daily-checkin", {
    params,
    headers: getAuthHeader(),
  });

  if (!response.data.success || !Array.isArray(response.data.data)) {
    throw new Error(
      response.data.message || "Failed to load daily check-in report",
    );
  }

  return response.data.data;
};

export interface DailyCheckInExportRequest {
  fromDate: string;
  toDate: string;
  venueIds: number[];
  propertyIds: number[];
  specificTourId: number | null;
  pageNumber: number;
  pageSize: number;
}

export interface DailyCheckInExportFile {
  blob: Blob;
  fileName: string;
}

export interface DailyCheckInTourFilterOptionApi {
  tourId: number;
  tourName: string;
  officeName: string;
  noOfApplicants: number;
  displayText: string;
}

interface DailyCheckInTourFilterOptionsResponse {
  success: boolean;
  message: string;
  data: DailyCheckInTourFilterOptionApi[];
  errors?: unknown[];
}

export interface AllocateAccommodationRoomPayload {
  roomAllocationPublicId: string;
  roomNumber: string;
  roomTypePublicId: string;
}

export interface AllocateAccommodationRoomResponse {
  success: boolean;
  message: string;
  data?: Record<string, unknown>;
  errors?: unknown[];
}

export interface UnallocateAccommodationRoomResponse {
  success: boolean;
  message: string;
  data: boolean;
  errors?: string[];
}

export interface ReallocateAccommodationRoomPayload {
  roomAllocationPublicId: string;
  destinationPropertyPublicId: string;
  destinationRoomTypePublicId: string;
  destinationRoomNumber: string;
  allDays: boolean;
  fromDate: string;
  toDate: string;
  reason: string;
}

export type ReallocateAccommodationRoomResponse =
  AllocateAccommodationRoomResponse;

export interface AvailableAccommodationPropertyApi {
  propertyPublicId: string;
  propertyName: string;
}

export interface AvailableAccommodationPropertiesResponse {
  success: boolean;
  message: string;
  data: AvailableAccommodationPropertyApi[];
  errors: unknown[];
}

export interface AccommodationRoomTypeOptionApi {
  id: number;
  publicId: string;
  code: string;
  roomName: string;
  description: string;
  isActive: boolean;
}

export interface AvailableAccommodationRoomApi {
  roomPublicId: string;
  roomNumber: string;
  isBooked: boolean;
}

export interface AvailableAccommodationRoomsResponse {
  success: boolean;
  message: string;
  data: AvailableAccommodationRoomApi[];
  errors: unknown[];
}

export type AutoAssignAccommodationRoomsResponse =
  AllocateAccommodationRoomResponse;

export interface PropertyTypeApi {
  id: number;
  publicId: string;
  name: string;
  code: string;
  isActive: boolean;
}

export const getAuthHeader = () => {
  const token = localStorage.getItem("fhims_auth_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const getPropertyTypesReallocate = async (): Promise<PropertyTypeApi[]> => {
  const res = await axiosInstance.get("/property-types?isActive=true", {
    headers: getAuthHeader(),
  });

  return res.data;
};

export const getAccommodationDashboard = async (params: {
  fromDate?: string;
  toDate?: string;
  venuePublicId?: string;
  status?: number;
  search?: string;
}): Promise<AccommodationDashboardDataApi> => {
  const response = await axiosInstance.get("/tours/accommodation", {
    params: {
      FromDate: params.fromDate || undefined,
      ToDate: params.toDate || undefined,
      VenuePublicId: params.venuePublicId || undefined,
      Status: params.status,
      Search: params.search || undefined,
    },
    headers: getAuthHeader(),
  });
  return response.data.data;
};

export const getDailyCheckInSummaryStats = async (
  selectedDate: string,
): Promise<DailyCheckInSummaryStatsApi> => {
  const response = await axiosInstance.get<DailyCheckInSummaryStatsResponse>(
    "/DailyCheckInReport/summary-stats",
    {
      params: {
        selectedDate: `${selectedDate}T00:00:00`,
      },
      headers: getAuthHeader(),
    },
  );

  if (!response.data.success || !response.data.data) {
    throw new Error(
      response.data.message || "Failed to load daily check-in summary",
    );
  }

  return response.data.data;
};

export const getDailyCheckInTourFilterOptions = async (params: {
  venueId?: number;
  propertyId?: number;
}): Promise<DailyCheckInTourFilterOptionApi[]> => {
  const response =
    await axiosInstance.get<DailyCheckInTourFilterOptionsResponse>(
      "/DailyCheckInReport/filter-options-tours",
      {
        params: {
          venueId: params.venueId,
          propertyId: params.propertyId,
        },
        headers: getAuthHeader(),
      },
    );

  if (!response.data.success || !Array.isArray(response.data.data)) {
    throw new Error(
      response.data.message || "Failed to load arriving tour options",
    );
  }

  return response.data.data;
};

const getExportFileName = (
  contentDisposition: string | undefined,
  fallback: string,
) => {
  if (!contentDisposition) return fallback;

  const encodedFileName = contentDisposition.match(
    /filename\*=UTF-8''([^;]+)/i,
  )?.[1];
  const fileName =
    encodedFileName ?? contentDisposition.match(/filename="?([^";]+)"?/i)?.[1];

  if (!fileName) return fallback;

  try {
    return decodeURIComponent(fileName.trim().replace(/^"|"$/g, ""));
  } catch {
    return fileName.trim().replace(/^"|"$/g, "");
  }
};

const exportDailyCheckIn = async (
  format: "pdf" | "excel",
  payload: DailyCheckInExportRequest,
): Promise<DailyCheckInExportFile> => {
  const extension = format === "pdf" ? "pdf" : "xlsx";
  const mimeType =
    format === "pdf"
      ? "application/pdf"
      : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
  const response = await axiosInstance.post<Blob>(
    `/Export/daily-checkin/${format}`,
    payload,
    {
      headers: {
        ...getAuthHeader(),
        Accept: mimeType,
      },
      responseType: "blob",
    },
  );

  return {
    blob: new Blob([response.data], { type: mimeType }),
    fileName: getExportFileName(
      response.headers["content-disposition"],
      `daily-check-in-${payload.fromDate.slice(0, 10)}-to-${payload.toDate.slice(0, 10)}.${extension}`,
    ),
  };
};

export const exportDailyCheckInPdf = (payload: DailyCheckInExportRequest) =>
  exportDailyCheckIn("pdf", payload);

export const exportDailyCheckInExcel = (payload: DailyCheckInExportRequest) =>
  exportDailyCheckIn("excel", payload);

export const allocateAccommodationRoom = async (
  payload: AllocateAccommodationRoomPayload,
): Promise<AllocateAccommodationRoomResponse> => {
  const response = await axiosInstance.post<AllocateAccommodationRoomResponse>(
    "/tours/accommodation",
    payload,
    { headers: getAuthHeader() },
  );
  return response.data;
};

export const unallocateAccommodationRoom = async (
  roomAllocationPublicId: string,
): Promise<UnallocateAccommodationRoomResponse> => {
  const response =
    await axiosInstance.delete<UnallocateAccommodationRoomResponse>(
      `/tours/accommodation/room-allocation/${encodeURIComponent(roomAllocationPublicId)}`,
      { headers: getAuthHeader() },
    );

  return response.data;
};

export const autoAssignAccommodationRooms = async (
  tourPublicId: string,
): Promise<AutoAssignAccommodationRoomsResponse> => {
  const response =
    await axiosInstance.post<AutoAssignAccommodationRoomsResponse>(
      `/tours/${encodeURIComponent(tourPublicId)}/accommodation/auto-assign`,
      undefined,
      { headers: getAuthHeader() },
    );
  return response.data;
};

// REALLOCATION APIs
//REALLOCATION NEW PROPERTY DROPDOWN API
export const getAvailableAccommodationProperties = async (params: {
  venuePublicId: string;
  propertyTypeId: string;
  startDate: string;
  endDate: string;
}): Promise<AvailableAccommodationPropertiesResponse> => {
  const response =
    await axiosInstance.get<AvailableAccommodationPropertiesResponse>(
      "/property/available-list",
      {
        params,
        headers: getAuthHeader(),
      },
    );

  return response.data;
};

//REALLOCATION ROOM TYPE DROPDOWN API
export const getAccommodationRoomTypesByProperty = async (
  propertyPublicId: string,
): Promise<AccommodationRoomTypeOptionApi[]> => {
  const response = await axiosInstance.get<AccommodationRoomTypeOptionApi[]>(
    `/room-type/byProperty/${encodeURIComponent(propertyPublicId)}`,
    { headers: getAuthHeader() },
  );

  return response.data;
};

//REALLOCATION AVAILABLE ROOMS API
export const getAvailableAccommodationRooms = async (params: {
  propertyPublicId: string;
  venuePublicId: string;
  startDate: string;
  endDate: string;
  roomTypePublicId: string;
}): Promise<AvailableAccommodationRoomsResponse> => {
  const response = await axiosInstance.get<AvailableAccommodationRoomsResponse>(
    "/property/available-rooms",
    {
      params,
      headers: getAuthHeader(),
    },
  );

  return response.data;
};

//REALLOCATION ROOM SAVE API
export const reallocateAccommodationRoom = async (
  tourPublicId: string,
  payload: ReallocateAccommodationRoomPayload,
): Promise<ReallocateAccommodationRoomResponse> => {
  const response =
    await axiosInstance.post<ReallocateAccommodationRoomResponse>(
      `/tours/${encodeURIComponent(tourPublicId)}/accommodation/reallocate`,
      payload,
      { headers: getAuthHeader() },
    );

  return response.data;
};
